import { Field, FieldProps } from "formik";
import { useState } from "react";
import { Link } from "react-router-dom";

// import { FormControlLabel, FormLabel, Radio, RadioGroup } from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import CancelCircleIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { Alert } from "@material-ui/lab";

import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  CalleeField,
  PollingPlaceLookupTool
} from "src/graphql/generated/globalTypes";
import palette from "src/styles/palette";

const useStyles = makeStyles(theme => ({
  fieldName: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  fieldLabel: {
    paddingLeft: "5px"
  },
  fieldList: {
    marginBottom: theme.spacing(4)
  },
  embedPreviewWrapper: {
    backgroundColor: palette.lightGray,
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingBottom: "24px",
    textAlign: "center"
  },
  embedPreviewHeader: {
    marginTop: "6px",
    marginBottom: "6px",
    color: palette.darkGray
  },
  embedPreviewImage: {
    width: "200px",
    height: "312px"
  },
  pollingPlaceWarning: {
    marginBottom: theme.spacing(3)
  },
  flexWrapper: {
    display: "flex",
    columnGap: "60px"
  }
}));

const MappedIcon = () => <CheckCircleIcon htmlColor={palette.green} />;
const UnmappedIcon = () => <CancelCircleIcon htmlColor={palette.darkGray} />;

function AddressField({
  builtInFieldId,
  campaign,
  hideIfNotMapped
}: {
  builtInFieldId: CalleeField;
  campaign: getCampaign_campaign;
  hideIfNotMapped?: boolean;
}) {
  const classes = useStyles();

  const field = campaign.calleeFields.find(
    field => field.builtInField === builtInFieldId
  );
  if (!field) {
    return null;
  }

  if (!field.dataSourceFieldName && hideIfNotMapped) {
    return null;
  }

  return (
    <div className={classes.fieldName}>
      {field.dataSourceFieldName ? <MappedIcon /> : <UnmappedIcon />}
      <div className={classes.fieldLabel}>{field.name}</div>
    </div>
  );
}

export default function PollingPlaceLookupSettings({
  campaign,
  lookupToolFieldName
}: {
  campaign: getCampaign_campaign;
  lookupToolFieldName: string;
}): JSX.Element {
  const classes = useStyles();

  const [
    routeLeavingGuardMessageOverride,
    setRouteLeavingGuardMessageOverride
  ] = useState<string | null>(null);

  return (
    <Field name={lookupToolFieldName}>
      {({ field, form }: FieldProps) => (
        <>
          {/* <FormLabel id="polling-place-lookup-tool-radio-lable">
            What polling place locator tool would you like to use?{" "}
            <Link to="TODO">
              Learn more about the different polling place locators.
            </Link>
          </FormLabel>
          <RadioGroup
            aria-labelledby="polling-place-lookup-tool-radio-lable"
            name={lookupToolFieldName}
            value={field.value}
            onChange={field.onChange}
          >
            <FormControlLabel
              key={PollingPlaceLookupTool.IWILLVOTE}
              value={PollingPlaceLookupTool.IWILLVOTE}
              label="I Will Vote"
              control={<Radio color="primary" />}
            />
            // TODO: other tools?
          </RadioGroup> */}
          <RouteLeavingGuard
            when={form.dirty}
            body={routeLeavingGuardMessageOverride || undefined}
            onCancel={() => setRouteLeavingGuardMessageOverride(null)}
          />
          <div className={classes.flexWrapper}>
            <div>
              <Alert severity="warning" className={classes.pollingPlaceWarning}>
                <strong>Warning:</strong> The polling place, ballot dropbox, and
                early voting location information provided in the tool comes
                directly from IWillVote.com. It does not come from, nor can it
                be edited by, Scale to Win.
                <br />
                <br />
                IWillVote.com is typically updated around major elections. Check
                with your state Democratic Party to determine the accuracy of
                IWillVote.com's Polling Place Locator in your state before using
                this question type.
              </Alert>
              <div className={classes.fieldList}>
                The polling place lookup form will be pre-filled based on your
                campaign's{" "}
                <Link
                  to="contacts"
                  onClick={() =>
                    setRouteLeavingGuardMessageOverride(
                      "This will redirect you to the Add Contacts page where you can adjust your mapped contact fields. Are you sure you want to discard your current changes?"
                    )
                  }
                >
                  mapped contact fields
                </Link>
                <AddressField
                  builtInFieldId={CalleeField.ADDRESS_LINE_1}
                  campaign={campaign}
                />
                <AddressField
                  builtInFieldId={CalleeField.ADDRESS_LINE_2}
                  campaign={campaign}
                  hideIfNotMapped
                />
                <AddressField
                  builtInFieldId={CalleeField.ZIP}
                  campaign={campaign}
                />
              </div>
            </div>
            <div className={classes.embedPreviewWrapper}>
              <h4 className={classes.embedPreviewHeader}>SAMPLE</h4>
              {field.value === PollingPlaceLookupTool.IWILLVOTE && (
                <img
                  className={classes.embedPreviewImage}
                  src="/iwillvote_preview.png"
                />
              )}
            </div>
          </div>
        </>
      )}
    </Field>
  );
}
